// The uploaded icons a design uses, out of CustomizerScreen and generalHelpers.js (backfill
// PR 25): the ids of the uploaded icon assets a design's keycaps show (the customizer, the
// place-order page and the order processing load their render assets by them), whether a
// design shows one (the cart's designs are checked before an uploaded icon is deleted), and
// a design without the entries that show a deleted one (the customizer cleans the current
// design and both history stacks). The ids are compared as strings on both sides (an entry
// stores the id as a string, and the icon library's delete handler passes one).
import { normalizeIconObject } from './iconEntries';

const uploadedAssetId = (iconObj) => {
  const normalizedIcon = normalizeIconObject(iconObj); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be checked with one shape

  return normalizedIcon.source === 'user_asset' && normalizedIcon.assetId
    ? String(normalizedIcon.assetId)
    : null;
}; // the id of the uploaded icon an entry shows, as a string, or null for a library icon (the three functions below spelled this check out, and defaulted the source normalizeIconObject had already defaulted, until PR 25)

export const collectUserIconAssetIds = (design) => {
  const assetIds = new Set();

  design.keycapData.forEach((row) => {
    row.forEach((keycap) => {
      if (keycap.isSpacer || !keycap.icons) {
        return;
      }

      Object.values(keycap.icons).forEach((iconObj) => {
        const assetId = uploadedAssetId(iconObj);

        if (assetId) {
          assetIds.add(assetId);
        }
      });
    });
  });

  return [...assetIds];
}; // collect all uploaded icon assetIds that are currently used inside the design

export const designUsesUserIconAsset = (design, assetId) =>
  collectUserIconAssetIds(design).includes(String(assetId)); // checks whether a design references a specific uploaded icon asset (over the whole design rather than stopping at the first match: a few hundred entries at most, once per cart design when an uploaded icon is deleted; user decision, Oct 2026)

export const removeUserIconAssetFromDesign = (design, deletedAssetId) => {
  const deletedAssetIdString = String(deletedAssetId); // normalize the deleted icon id so it can be compared with stored asset ids
  let changed = false;

  const nextKeycapData = design.keycapData.map((row) =>
    row.map((keycap) => {
      if (keycap.isSpacer || !keycap.icons) {
        return keycap;
      }

      let keycapChanged = false; // tracks whether this specific keycap lost an icon

      const nextIcons = Object.entries(keycap.icons).reduce(
        (acc, [iconKey, iconObj]) => {
          const shouldRemoveIcon =
            uploadedAssetId(iconObj) === deletedAssetIdString;

          if (shouldRemoveIcon) {
            changed = true;
            keycapChanged = true;
            return acc; // skip this icon entry because it references the deleted uploaded icon
          }

          acc[iconKey] = iconObj;
          return acc;
        },
        {}
      );

      if (!keycapChanged) {
        return keycap; // keep this keycap object unchanged if none of its icons were removed
      }

      return {
        ...keycap,
        icons: nextIcons,
      };
    })
  );

  if (!changed) {
    return {
      design,
      changed: false,
    };
  }

  return {
    design: {
      ...design,
      keycapData: nextKeycapData,
    },
    changed: true,
  };
}; // removes all icon entries from a design that reference the deleted uploaded icon assetId
